import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '../ui/Dialog';
import { useCamera } from '../../hooks/useCamera';
import { useIsMobile } from '../../hooks/useIsMobile';
import { playerAPI } from '../../services/api';

interface AvatarCaptureProps {
  playerId: string;
  playerName: string;
  gameId: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function AvatarCapture({ playerId, playerName, gameId, onClose, onSaved }: AvatarCaptureProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { videoRef, isActive, error, startCamera, stopCamera, capturePhoto } = useCamera();
  const [capturedPhotos, setCapturedPhotos] = useState<Blob[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  // Start on a choice screen (gallery vs camera) so we DON'T demand camera permission up front —
  // the camera only turns on if the user actually chooses to take a photo.
  const [mode, setMode] = useState<'choose' | 'camera'>('choose');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // No auto camera start — see `mode`. Only clean up on unmount.
    return () => {
      stopCamera();
      // Cleanup preview URLs
      photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const enterCameraMode = () => {
    setMode('camera');
    startCamera();
  };
  const backToChoice = () => {
    stopCamera();
    setMode('choose');
  };

  // Let the user back out with Escape as well as the ✕ / Cancel buttons.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Pick existing photos from the gallery/files instead of (or in addition to) the camera.
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = Math.max(0, 5 - capturedPhotos.length);
    const chosen = files.slice(0, room);
    setCapturedPhotos(prev => [...prev, ...chosen]);
    setPhotoPreviewUrls(prev => [...prev, ...chosen.map(f => URL.createObjectURL(f))]);
    e.target.value = ''; // allow re-picking the same file later
  };

  const handleCapturePhoto = async () => {
    try {
      const photoBlob = await capturePhoto();
      setCapturedPhotos(prev => [...prev, photoBlob]);

      // Create preview URL
      const previewUrl = URL.createObjectURL(photoBlob);
      setPhotoPreviewUrls(prev => [...prev, previewUrl]);
    } catch (err) {
      console.error('Failed to capture photo:', err);
    }
  };

  const handleRemovePhoto = (index: number) => {
    URL.revokeObjectURL(photoPreviewUrls[index]);
    setCapturedPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (capturedPhotos.length === 0) {
      alert(t('avatarCapture.alert'));
      return;
    }

    try {
      setIsUploading(true);

      // Convert blobs to Files
      const files = capturedPhotos.map((blob, index) =>
        new File([blob], `photo-${index + 1}.jpg`, { type: 'image/jpeg' })
      );

      await playerAPI.uploadAvatar(gameId, playerId, files);
      onSaved();
    } catch (error: any) {
      console.error('Upload failed:', error);
      alert(t('avatarCapture.uploadError', { error: error.response?.data?.error || error.message }));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      title={t('avatarCapture.title')}
      labelledBy="avatar-capture-title"
      onClose={onClose}
      busy={isUploading}
      maxWidth="900px"
      padding={isMobile ? '1.1rem' : '2rem'}
    >
      <div>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}>
          <div>
            <h2 id="avatar-capture-title" style={{ fontSize: '1.8rem', marginBottom: '0.25rem', color: '#e2e8f0' }}>
              {t('avatarCapture.title')}
            </h2>
            <p style={{ color: '#a0aec0', fontSize: '0.95rem' }}>
              {t('avatarCapture.player')} <strong>{playerName}</strong>
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '1px solid rgba(239, 68, 68, 0.5)',
              background: 'rgba(239, 68, 68, 0.1)',
              color: '#fca5a5',
              fontSize: '1.5rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '1.25rem' : '2rem' }}>
          {/* Left column: pick a source first (no camera permission until chosen), then shoot */}
          <div>
            {/* Hidden file input — the gallery option triggers it in either mode */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFilesSelected}
              style={{ display: 'none' }}
            />

            {/* Instructions (shown in both modes) */}
            <div style={{
              padding: '1rem',
              background: 'rgba(99, 102, 241, 0.1)',
              borderRadius: '12px',
              border: '1px solid rgba(99, 102, 241, 0.2)',
              marginBottom: '1rem',
            }}>
              <p style={{ fontSize: '0.9rem', color: '#cbd5e0', marginBottom: '0.5rem' }}>
                <strong>{t('avatarCapture.recommendations')}</strong>
              </p>
              <ul style={{ fontSize: '0.85rem', color: '#a0aec0', paddingLeft: '1.5rem', margin: 0 }}>
                <li>{t('avatarCapture.tip1')}</li>
                <li>{t('avatarCapture.tip2')}</li>
                <li>{t('avatarCapture.tip3')}</li>
                <li>{t('avatarCapture.tip4')}</li>
              </ul>
            </div>

            {mode === 'choose' ? (
              /* Source choice — gallery OR camera, up front */
              <div style={{ display: 'grid', gap: '0.75rem' }}>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={capturedPhotos.length >= 5}
                  style={{
                    width: '100%', padding: '1rem', fontSize: '1.05rem', fontWeight: 600,
                    borderRadius: '12px', border: 'none',
                    background: capturedPhotos.length >= 5 ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff', cursor: capturedPhotos.length >= 5 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t('avatarCapture.pickFromGallery')}
                </button>
                <button
                  onClick={enterCameraMode}
                  disabled={capturedPhotos.length >= 5}
                  style={{
                    width: '100%', padding: '1rem', fontSize: '1.05rem', fontWeight: 600,
                    borderRadius: '12px', border: '1px solid rgba(99, 102, 241, 0.5)',
                    background: 'rgba(99,102,241,0.12)', color: '#c7d2fe',
                    cursor: capturedPhotos.length >= 5 ? 'not-allowed' : 'pointer',
                  }}
                >
                  {t('avatarCapture.useCamera')}
                </button>
              </div>
            ) : (
              /* Camera mode — permission requested only now */
              <>
                <div style={{
                  position: 'relative',
                  background: '#000',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  marginBottom: '1rem',
                  border: '2px solid rgba(99, 102, 241, 0.3)',
                }}>
                  {error ? (
                    <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#fca5a5' }}>
                      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                      <p>{t('avatarCapture.cameraError')}</p>
                      <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>{error}</p>
                    </div>
                  ) : (
                    <>
                      <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: 'auto', display: 'block' }} />
                      {!isActive && (
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#fff', fontSize: '1.5rem' }}>
                          {t('avatarCapture.cameraLoading')}
                        </div>
                      )}
                    </>
                  )}
                </div>

                <button
                  onClick={handleCapturePhoto}
                  disabled={!isActive || capturedPhotos.length >= 5}
                  style={{
                    width: '100%', padding: '1rem', fontSize: '1.1rem', fontWeight: 'bold',
                    border: 'none', borderRadius: '12px',
                    background: (!isActive || capturedPhotos.length >= 5) ? 'rgba(99, 102, 241, 0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff', cursor: (!isActive || capturedPhotos.length >= 5) ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s',
                  }}
                >
                  {t('avatarCapture.takePhoto')} ({capturedPhotos.length}/5)
                </button>

                <button
                  onClick={backToChoice}
                  style={{
                    width: '100%', marginTop: '0.75rem', padding: '0.75rem', fontSize: '0.95rem',
                    borderRadius: '12px', border: '1px solid rgba(148,163,184,0.3)',
                    background: 'rgba(148,163,184,0.1)', color: '#cbd5e0', cursor: 'pointer',
                  }}
                >
                  ← {t('avatarCapture.back')}
                </button>
              </>
            )}
          </div>

          {/* Captured Photos */}
          <div>
            <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#e2e8f0' }}>
              {t('avatarCapture.capturedPhotos')} ({capturedPhotos.length})
            </h3>

            {capturedPhotos.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '3rem 1rem',
                color: '#718096',
                background: 'rgba(0, 0, 0, 0.2)',
                borderRadius: '12px',
                border: '2px dashed rgba(99, 102, 241, 0.3)',
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🖼️</div>
                <p>{t('avatarCapture.photosHere')}</p>
              </div>
            ) : (
              <>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1.5rem',
                }}>
                  {photoPreviewUrls.map((url, index) => (
                    <div
                      key={index}
                      style={{
                        position: 'relative',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        border: '2px solid rgba(99, 102, 241, 0.3)',
                        animation: 'fadeIn 0.3s ease-out',
                      }}
                    >
                      <img
                        src={url}
                        alt={`Photo ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                      <button
                        onClick={() => handleRemovePhoto(index)}
                        style={{
                          position: 'absolute',
                          top: '0.5rem',
                          right: '0.5rem',
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          border: 'none',
                          background: 'rgba(239, 68, 68, 0.9)',
                          color: '#fff',
                          fontSize: '1rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        ✕
                      </button>
                      <div style={{
                        position: 'absolute',
                        bottom: '0.5rem',
                        left: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        color: '#fff',
                      }}>
                        #{index + 1}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={handleUpload}
                  disabled={isUploading}
                  style={{
                    width: '100%',
                    padding: '1.25rem',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    border: 'none',
                    borderRadius: '12px',
                    background: isUploading
                      ? 'rgba(16, 185, 129, 0.3)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: '#fff',
                    cursor: isUploading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isUploading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 15px 35px rgba(16, 185, 129, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  {isUploading ? t('avatarCapture.uploading') : t('avatarCapture.save')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Explicit way out — in case the reader changed their mind about adding a photo. */}
        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '1.25rem',
            padding: '0.85rem',
            fontSize: '1rem',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.06)',
            color: '#e2e8f0',
            cursor: 'pointer',
          }}
        >
          {t('avatarCapture.cancel')}
        </button>
      </div>
    </Dialog>
  );
}
